import shuffle from "lodash-es/shuffle";
import { BookOfMindQuote } from "motw_core/DTOs/BookOfMindQuote";
import type { IBookOfMindPageAudioDiagnostics } from "source/app/services/External/LogicServer/IBookOfMindPageAudioDiagnostics";
import {
    BookOfMindDisplayOrder,
    IBookOfMindState,
} from "./IBookOfMindState";

interface IPendingBookOfMindDisplayOrder {
    rootNodeName: string;
    chronologicalQuotes: BookOfMindQuote[];
    searchText: string;
    displayOrder: BookOfMindDisplayOrder;
    quotesForDisplay: BookOfMindQuote[];
}

let pendingBookOfMindDisplayOrder: IPendingBookOfMindDisplayOrder | undefined;

export function GetBookOfMindQuotesForDisplay(
    chronologicalQuotes: BookOfMindQuote[],
    displayOrder: BookOfMindDisplayOrder,
    searchText: string
) {
    const orderedQuotes =
        displayOrder === "random"
            ? shuffle(chronologicalQuotes)
            : displayOrder === "newest"
              ? [...chronologicalQuotes].reverse()
              : chronologicalQuotes;

    return searchText !== ""
        ? orderedQuotes.filter((quote) => quote.Contains(searchText))
        : orderedQuotes;
}

export function SetPendingBookOfMindDisplayOrder(
    state: IBookOfMindState,
    displayOrder: BookOfMindDisplayOrder,
    quotesForDisplay: BookOfMindQuote[]
) {
    pendingBookOfMindDisplayOrder = {
        rootNodeName: state.rootNodeName,
        chronologicalQuotes: state.chronologicalQuotes,
        searchText: state.searchText,
        displayOrder,
        quotesForDisplay,
    };
}

export function GetBookOfMindStateForPageAudio(state: IBookOfMindState) {
    const pendingDisplayOrder = pendingBookOfMindDisplayOrder;
    if (
        !pendingDisplayOrder ||
        pendingDisplayOrder.rootNodeName !== state.rootNodeName ||
        pendingDisplayOrder.chronologicalQuotes !== state.chronologicalQuotes ||
        pendingDisplayOrder.searchText !== state.searchText ||
        pendingDisplayOrder.displayOrder === state.displayOrder
    ) {
        return state;
    }

    return {
        ...state,
        displayOrder: pendingDisplayOrder.displayOrder,
        remainingQuotesToDisplay: pendingDisplayOrder.quotesForDisplay,
        totalQuotesToDisplay: pendingDisplayOrder.quotesForDisplay.length,
    };
}

export function GetBookOfMindPageAudioDiagnostics(
    state: IBookOfMindState
): IBookOfMindPageAudioDiagnostics {
    const pageQuotes = state.remainingQuotesToDisplay.slice(
        0,
        state.numberOfQuotesToDisplay
    );
    const quoteIds = pageQuotes.map((quote) => quote.id);
    const quoteHashes = pageQuotes.map((quote) =>
        HashBookOfMindText(quote.quote.trim())
    );
    const pageStartIndex = Math.max(
        state.totalQuotesToDisplay - state.remainingQuotesToDisplay.length,
        0
    );

    return {
        displayOrder: state.displayOrder,
        pageStartIndex,
        pageSize: state.numberOfQuotesToDisplay,
        totalQuotesToDisplay: state.totalQuotesToDisplay,
        remainingQuotesToDisplay: state.remainingQuotesToDisplay.length,
        quoteCount: pageQuotes.length,
        quoteIds,
        quoteHashes,
        pageSignature: HashBookOfMindText(
            JSON.stringify({
                displayOrder: state.displayOrder,
                pageStartIndex,
                quoteIds,
                quoteHashes,
            })
        ),
    };
}

export function HashBookOfMindText(text: string) {
    let hash = 2166136261;

    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(16).padStart(8, "0");
}
